import { useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Ticket, Check, Loader2, Sparkles, X } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { useAuth } from "../lib/auth";

/**
 * Compact "Have a promo code?" box.
 *
 * Props:
 *   variant  — "inline" (single-row on Wallet) or "card" (glass card on Pricing)
 *   onRedeemed — callback fired after a successful redemption (e.g. refresh auth)
 *   compact   — hides the intro copy in card variant
 */
export default function PromoRedeemBox({ variant = "card", onRedeemed, compact = false }) {
  const { refresh, user } = useAuth();
  const [code, setCode] = useState("");
  const [preview, setPreview] = useState(null); // { code, plan, duration_months, notes }
  const [checking, setChecking] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null); // { months, granted_until }

  const doCheck = async () => {
    setError(null); setPreview(null);
    const c = code.trim();
    if (!c) return;
    if (!user) {
      setError("Log in to redeem a code");
      return;
    }
    setChecking(true);
    try {
      const r = await api.get(`/promo/${encodeURIComponent(c)}/preview`);
      setPreview(r.data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Couldn't validate that code");
    } finally { setChecking(false); }
  };

  const doRedeem = async () => {
    if (!preview) return;
    setRedeeming(true);
    setError(null);
    try {
      const r = await api.post("/promo/redeem", { code: preview.code });
      setSuccess({ months: r.data?.months, granted_until: r.data?.granted_until, plan: r.data?.plan });
      toast.success(`Unlocked ${r.data?.months} months of Pro 🎉`);
      setPreview(null);
      setCode("");
      try { await refresh(); } catch { /* ignore */ }
      onRedeemed?.(r.data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Redemption failed");
    } finally { setRedeeming(false); }
  };

  const clearAll = () => { setCode(""); setPreview(null); setError(null); setSuccess(null); };

  // Success state (shows after redemption, replaces the input)
  if (success) {
    const until = success.granted_until ? new Date(success.granted_until).toLocaleDateString("en-GB", {
      day: "numeric", month: "short", year: "numeric",
    }) : "";
    return (
      <div
        className={variant === "card"
          ? "glass rounded-2xl p-5 border border-emerald-500/25 bg-emerald-500/5"
          : "rounded-xl p-4 border border-emerald-500/25 bg-emerald-500/10"}
        data-testid="promo-redeem-success"
      >
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-lg bg-emerald-500/20 flex items-center justify-center shrink-0">
            <Sparkles className="h-4 w-4 text-emerald-300" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-emerald-300 mb-1">Pro unlocked</div>
            <div className="font-display font-bold text-lg leading-tight text-white">
              {success.months} months of Pro on the house
            </div>
            {until && <div className="text-sm text-zinc-400 mt-1">Active until {until}</div>}
          </div>
          <button
            onClick={clearAll}
            className="text-zinc-500 hover:text-white h-8 w-8 flex items-center justify-center rounded-full hover:bg-white/5"
            aria-label="Dismiss"
            data-testid="promo-redeem-dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  const inputRow = (
    <div className="flex gap-2">
      <div className="relative flex-1">
        <Ticket className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-nexus-pink pointer-events-none" />
        <Input
          value={code}
          onChange={(e) => { setCode(e.target.value.toUpperCase().slice(0, 32)); setError(null); setPreview(null); }}
          placeholder="e.g. WELCOMEPRO3M"
          className="bg-white/[0.02] border-white/10 text-white pl-9 h-10 uppercase tracking-wider font-mono"
          data-testid="promo-code-input"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); preview ? doRedeem() : doCheck(); } }}
        />
      </div>
      {!preview ? (
        <Button
          onClick={doCheck}
          disabled={checking || !code.trim()}
          variant="outline"
          className="rounded-full border-white/10 text-white hover:bg-white/5 h-10 px-4 font-semibold disabled:opacity-50"
          data-testid="promo-check-btn"
        >
          {checking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Check"}
        </Button>
      ) : (
        <Button
          onClick={doRedeem}
          disabled={redeeming}
          className="nexus-gradient text-white border-0 rounded-full h-10 px-4 font-semibold disabled:opacity-60"
          data-testid="promo-redeem-btn"
        >
          {redeeming ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Redeeming…</> : <><Check className="h-3.5 w-3.5 mr-1.5" /> Redeem</>}
        </Button>
      )}
    </div>
  );

  const previewBanner = preview && (
    <div className="mt-3 p-3 rounded-lg bg-white/[0.02] border border-emerald-500/20 flex items-start gap-2.5" data-testid="promo-code-preview">
      <div className="h-8 w-8 rounded-lg bg-emerald-500/15 flex items-center justify-center shrink-0">
        <Sparkles className="h-4 w-4 text-emerald-300" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-emerald-300">Valid code</div>
        <div className="text-sm text-white font-semibold">
          {preview.duration_months} months of {preview.plan === "brand_pro" ? "Brand Pro" : "Creator Pro"}
        </div>
        {preview.notes && <div className="text-[11px] text-zinc-500 mt-0.5">{preview.notes}</div>}
      </div>
    </div>
  );

  const errorBanner = error && (
    <div className="mt-2 text-[11px] text-red-300 flex items-center gap-1.5" data-testid="promo-code-error">
      <X className="h-3 w-3" /> {error}
    </div>
  );

  if (variant === "inline") {
    return (
      <div className="rounded-xl p-3 border border-white/10 bg-white/[0.02]" data-testid="promo-redeem-inline">
        <div className="flex items-center gap-1.5 mb-2 text-[10px] uppercase tracking-widest text-zinc-500">
          <Ticket className="h-3 w-3 text-nexus-pink" /> Have a promo code?
        </div>
        {inputRow}
        {previewBanner}
        {errorBanner}
      </div>
    );
  }

  return (
    <div className="glass rounded-2xl p-5" data-testid="promo-redeem-card">
      <div className="flex items-center gap-2 mb-2">
        <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-nexus-pink to-nexus-purple flex items-center justify-center">
          <Ticket className="h-4 w-4 text-white" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Promo code</div>
          <div className="font-display font-bold text-lg leading-tight">Got a launch code?</div>
        </div>
      </div>
      {!compact && (
        <p className="text-[13px] text-zinc-400 mb-3">
          If a Nexus partner gave you a code, drop it in below to unlock free months of Pro instantly.
        </p>
      )}
      {inputRow}
      {previewBanner}
      {errorBanner}
    </div>
  );
}
